import { ref } from "vue";
import { solveAltcha, AltchaClientError } from "@/services/altchaService";
import { submitDonation } from "@/services/donationFormService";
import { resolveDonationError } from "@/services/donationErrorMap";
import { useFunnel } from "@/composables/useFunnel";
import { logger } from "@/services/loggerService";
// The solver awaits every digest, which is what lets the timeout win the race.
const ALTCHA_TIMEOUT_MS = 15000;
/** Mirrors the backend return-URL cap: a longer URL is refused and the return page with it. */
const RETURN_URL_MAX_LENGTH = 2048;
const RETURN_URL_REFUSAL_CODES = new Set([
    "Submission.ReturnUrl.Invalid",
    "Submission.ReturnUrl.NotAllowed",
]);
function extractErrorCode(e) {
    const code = e?.response?.data
        ?.errorCode;
    return typeof code === "string" ? code : null;
}
// CONSTRAINT: opdonationform accepts this URL only because its origin is the one it is configured
// with as PUBLIC_FORM_BASE_URL. Served from another origin, the donor site loses its return page:
// the URL is refused and the donation is resubmitted without it.
function resolveReturnUrl(slug, attributionToken) {
    const base = new URL(`/forms/${encodeURIComponent(slug)}/return`, window.location.origin).toString();
    if (typeof attributionToken !== "string" || attributionToken.trim() === "") {
        return base;
    }
    // URLSearchParams leaves `*` raw and the backend's return-URL pre-parse rejects it.
    const query = new URLSearchParams({ at: attributionToken }).toString().replace(/\*/g, "%2A");
    const withToken = `${base}?${query}`;
    // Drop the token, never the return page: a missing `at` costs only the retry's attribution.
    return withToken.length <= RETURN_URL_MAX_LENGTH ? withToken : base;
}
// Assigned verbatim: the link comes from the backend, never from donor input.
function redirectToPayment(paymentLink) {
    try {
        if (window.top && window.top !== window.self) {
            window.top.location.assign(paymentLink);
            return;
        }
    }
    catch {
        // A cross-origin `window.top` (sandboxed embed) falls back to same-frame navigation.
    }
    window.location.assign(paymentLink);
}
export function useDonationSubmit(slug) {
    const errorMessage = ref(null);
    const funnel = useFunnel(slug);
    const solve = async () => {
        try {
            return await Promise.race([
                solveAltcha(),
                new Promise((_, reject) => setTimeout(() => reject(new AltchaClientError("unsolvable")), ALTCHA_TIMEOUT_MS)),
            ]);
        }
        catch (e) {
            const code = e instanceof AltchaClientError ? e.errorCode : null;
            errorMessage.value = resolveDonationError(code);
            throw e;
        }
    };
    const submit = async (body) => {
        const solution = await solve();
        try {
            const { paymentLink } = await submitDonation(slug, body, solution);
            // Dispatched before the redirect and never awaited: the beacon is in flight when the page
            // unloads.
            funnel.redirectedToPayment();
            redirectToPayment(paymentLink);
        }
        catch (e) {
            const code = extractErrorCode(e);
            // Value-free on purpose: the body carries donor PII and the attribution token, so log the
            // slug and the code only.
            if (code !== null && RETURN_URL_REFUSAL_CODES.has(code) && body.returnUrl !== undefined) {
                logger.error("Donation return URL refused, submitting without it", {
                    slug,
                    errorCode: code,
                });
                const { returnUrl: _refused, ...withoutReturnUrl } = body;
                return submit(withoutReturnUrl);
            }
            errorMessage.value = resolveDonationError(code);
            logger.error("Donation submit failed", { slug, errorCode: code ?? "unknown" });
            throw e;
        }
    };
    const onSubmit = async (req) => {
        errorMessage.value = null;
        // Emitted before ALTCHA: it counts donor intent even when ALTCHA or the POST fails.
        funnel.submitted();
        await submit({ ...req, returnUrl: resolveReturnUrl(slug, req.attributionToken) });
    };
    return { onSubmit, errorMessage };
}
